import { useMemo } from 'react'
import type { AppDispatch } from '@redux/store'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { EntityOptions } from '@redux/modules/types'
import * as serviceActions from '@redux/modules/service'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ServiceModel, ServiceRequestOptions } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['services']

type LoadServiceIdsParams = {
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
  serviceIds: number[],
}

const loadServiceIds = (params: LoadServiceIdsParams) => {
  const { dispatch, requestOptions, serviceIds } = params
  const { loadServiceIds: loadFn } = serviceActions

  return dispatch(loadFn(serviceIds, requestOptions))
}

type ServiceFilters = {
  string?: string,
}

type UseServicesOptions = {
  filters?: ServiceFilters,
  performHttpRequests?: boolean,
  requestOptions?: ServiceRequestOptions,
}

function useServices(options: UseServicesOptions) {
  const { filters = {}, requestOptions } = options
  const { string: filterString } = filters

  const {
    updatedEntities: { services: servicesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const dispatch = useDispatch()

  const { services } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredServices = useMemo(() => {
    const filtered = Object.values(services).filter((service: ServiceModel) => {
      const { id, title } = service

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [servicesUpdatedAt, JSON.stringify(filters)])

  const filteredServicesCount = filteredServices.length
  const hasFilteredServices = !!filteredServicesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredServicesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const reduxActionPayload = useReduxAction(
    'services',
    'loadServices',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    } as EntityOptions,
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingServices, reduxActionPerformed } = reduxActionPayload

  return {
    callbacks: {
      loadMore,
      loadServiceIds: (params: { serviceIds: number[], entityOptions?: EntityOptions }) => {
        const { serviceIds, entityOptions } = params

        return loadServiceIds({
          dispatch,
          requestOptions: entityOptions,
          serviceIds,
        })
      },
    },
    canLoadMore,
    filteredServices,
    filteredServicesCount,
    hasFilteredServices,
    loading: loadingServices,
    reduxActionPerformed,
    services,
  }
}

export default useServices
